import { API_BASE_URL, API_ENDPOINTS } from '@/shared/apiClient';
import { authService } from '@/features/auth/services/authService';

import { FileAttachment as FileData } from '@/shared/types/projectData';
export type { FileData };

export const HTML_ARTIFACT_CSP = "default-src 'none'; style-src 'unsafe-inline'; img-src data: blob:; font-src data:; script-src 'none'; connect-src 'none'; frame-src 'none'; child-src 'none'; object-src 'none'; base-uri 'none'; form-action 'none'";

// The file list routes accept at most 100 files per page; the page bound stops a runaway loop.
const FILE_LIST_PAGE_SIZE = 100;
const FILE_LIST_MAX_PAGES = 100;

export interface HtmlArtifactPreview {
  fileId: string;
  filename: string;
  mimeType: 'text/html';
  size: number;
  sha256: string;
  previewDocument: string;
  sandbox: '';
  referrerPolicy: 'no-referrer';
  contentSecurityPolicy: string;
  reviewStatus: string;
}

export class FileService {
  private static readonly ownedMediaPreviewUrls = new WeakMap<FileData, string>();

  static normalizeMimeType(mimeType: string): string {
    return mimeType.split(';', 1)[0].trim().toLowerCase();
  }

  // The original returned attachment is the ownership token; copies and URLs
  // supplied by callers are borrowed. Release only after all borrowers detach.
  // This is explicit disposal, not automatic cleanup or reference counting.
  static disposeMediaPreviews(files: readonly FileData[]): void {
    for (const file of files) {
      const objectUrl = this.ownedMediaPreviewUrls.get(file);
      if (!objectUrl) continue;
      this.ownedMediaPreviewUrls.delete(file);
      URL.revokeObjectURL(objectUrl);
    }
  }

  private static ownMediaPreviewUrl(file: FileData, objectUrl: string): FileData {
    file.thumbnailUrl = objectUrl;
    this.ownedMediaPreviewUrls.set(file, objectUrl);
    return file;
  }

  private static readonly HEIC_MIME_TYPES = new Set([
    'image/heic',
    'image/heif',
    'image/heic-sequence',
    'image/heif-sequence',
  ]);

  private static isHeicFile(file: File): boolean {
    const normalizedName = file.name.toLowerCase();
    return (
      this.HEIC_MIME_TYPES.has(this.normalizeMimeType(file.type || '')) ||
      normalizedName.endsWith('.heic') ||
      normalizedName.endsWith('.heif')
    );
  }

  private static async normalizeUploadFile(file: File): Promise<File> {
    if (!this.isHeicFile(file)) {
      return file;
    }

    const { convertHeicToJpeg } = await import('./convertHeicToJpeg');
    const converted = await convertHeicToJpeg(file, 0.92);

    const convertedBlob = Array.isArray(converted) ? converted[0] : converted;
    const normalizedName = file.name.replace(/\.(heic|heif)$/i, '.jpg');

    return new File([convertedBlob], normalizedName, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  }

  private static normalizeFile(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API response has dynamic shape
    rawFile: any,
    buildDownloadUrl: (fileId: string) => string
  ): FileData {
    const mimeType = this.normalizeMimeType(rawFile.mimeType ?? rawFile.mime_type ?? 'application/octet-stream');
    const uploadedByActor = rawFile.uploadedByActor ?? rawFile.uploaded_by_actor;
    const qualityCheck = rawFile.qualityCheck ?? rawFile.quality_check;
    const metadata = rawFile.metadata ?? {};
    const rawThumbnailUrl = rawFile.thumbnailUrl ?? rawFile.thumbnail_url;
    const thumbnailUrl = typeof rawThumbnailUrl === 'string' && rawThumbnailUrl.trim()
      ? this.toAbsoluteUrl(rawThumbnailUrl.trim())
      : undefined;

    return {
      id: rawFile.id,
      filename: rawFile.filename,
      originalName: rawFile.originalName ?? rawFile.original_name ?? rawFile.filename,
      mimeType,
      size: rawFile.size ?? 0,
      checksum: rawFile.checksum ?? '',
      uploadedAt: rawFile.uploadedAt ?? rawFile.uploaded_at ?? new Date().toISOString(),
      uploadedBy: rawFile.uploadedBy ?? rawFile.uploaded_by ?? '',
      uploadedByDisplay: rawFile.uploadedByDisplay ?? rawFile.uploaded_by_display,
      uploadedByActor: uploadedByActor ? {
        type: uploadedByActor.type ?? 'user',
        id: uploadedByActor.id ?? '',
        name: uploadedByActor.name,
        username: uploadedByActor.username,
        displayName: uploadedByActor.displayName ?? uploadedByActor.display_name ?? uploadedByActor.name ?? uploadedByActor.username ?? uploadedByActor.id ?? 'Unknown uploader',
        agentProfileId: uploadedByActor.agentProfileId ?? uploadedByActor.agent_profile_id,
        agentProfileName: uploadedByActor.agentProfileName ?? uploadedByActor.agent_profile_name,
        runId: uploadedByActor.runId ?? uploadedByActor.run_id,
        deviceId: uploadedByActor.deviceId ?? uploadedByActor.device_id,
        physicalHostId: uploadedByActor.physicalHostId ?? uploadedByActor.physical_host_id,
      } : undefined,
      qualityCheck: qualityCheck ? {
        status: qualityCheck.status ?? 'not_required',
        required: Boolean(qualityCheck.required),
        checkedBy: qualityCheck.checkedBy ?? qualityCheck.checked_by,
        checkedAt: qualityCheck.checkedAt ?? qualityCheck.checked_at,
        notes: qualityCheck.notes,
        label: qualityCheck.label,
        checksum: qualityCheck.checksum,
        evidence: Array.isArray(qualityCheck.evidence) ? qualityCheck.evidence : [],
        totalBytes: qualityCheck.totalBytes ?? qualityCheck.total_bytes,
        readComplete: qualityCheck.readComplete ?? qualityCheck.read_complete,
      } : undefined,
      artifactWorkflow: rawFile.artifactWorkflow ?? rawFile.artifact_workflow,
      status: rawFile.status ?? 'active',
      storagePath: rawFile.storagePath ?? rawFile.storage_path ?? '',
      url: rawFile.url
        ? (rawFile.url.startsWith('http') ? rawFile.url : `${API_BASE_URL}${buildDownloadUrl(rawFile.id)}`)
        : `${API_BASE_URL}${buildDownloadUrl(rawFile.id)}`,
      thumbnailUrl,
      metadata: {
        associatedDocIds: metadata.associatedDocIds ?? metadata.associated_doc_ids ?? [],
        tags: metadata.tags ?? [],
        description: metadata.description,
        sourceType: metadata.sourceType ?? metadata.source_type,
        sourceRunId: metadata.sourceRunId ?? metadata.source_run_id,
        sourceArtifactId: metadata.sourceArtifactId ?? metadata.source_artifact_id,
        sourceConversationId: metadata.sourceConversationId ?? metadata.source_conversation_id,
        sourceMessageId: metadata.sourceMessageId ?? metadata.source_message_id,
        projectAgentId: metadata.projectAgentId ?? metadata.project_agent_id,
        missionId: metadata.missionId ?? metadata.mission_id,
        missionPlanRevision: metadata.missionPlanRevision ?? metadata.mission_plan_revision,
        missionPlanHash: metadata.missionPlanHash ?? metadata.mission_plan_hash,
        contextManifestId: metadata.contextManifestId ?? metadata.context_manifest_id,
        contextManifestHash: metadata.contextManifestHash ?? metadata.context_manifest_hash,
        uploadActorType: metadata.uploadActorType ?? metadata.upload_actor_type,
        uploadActorId: metadata.uploadActorId ?? metadata.upload_actor_id,
        uploadActorName: metadata.uploadActorName ?? metadata.upload_actor_name,
        uploadActorUsername: metadata.uploadActorUsername ?? metadata.upload_actor_username,
        agentProfileId: metadata.agentProfileId ?? metadata.agent_profile_id,
        agentProfileName: metadata.agentProfileName ?? metadata.agent_profile_name,
        agentRunId: metadata.agentRunId ?? metadata.agent_run_id,
        agentDeviceId: metadata.agentDeviceId ?? metadata.agent_device_id,
        agentPhysicalHostId: metadata.agentPhysicalHostId ?? metadata.agent_physical_host_id,
        qualityCheckStatus: metadata.qualityCheckStatus ?? metadata.quality_check_status,
        qualityCheckedByType: metadata.qualityCheckedByType ?? metadata.quality_checked_by_type,
        qualityCheckedById: metadata.qualityCheckedById ?? metadata.quality_checked_by_id,
        qualityCheckedByName: metadata.qualityCheckedByName ?? metadata.quality_checked_by_name,
        qualityCheckedAt: metadata.qualityCheckedAt ?? metadata.quality_checked_at,
        qualityReviewNotes: metadata.qualityReviewNotes ?? metadata.quality_review_notes,
        qualityReviewChecksum: metadata.qualityReviewChecksum ?? metadata.quality_review_checksum,
        qualityReviewEvidence: metadata.qualityReviewEvidence ?? metadata.quality_review_evidence ?? [],
        qualityReviewTotalBytes: metadata.qualityReviewTotalBytes ?? metadata.quality_review_total_bytes,
        qualityReviewReadComplete: metadata.qualityReviewReadComplete ?? metadata.quality_review_read_complete,
      },
      accessCount: rawFile.accessCount ?? rawFile.access_count ?? 0,
      lastAccessedAt: rawFile.lastAccessedAt ?? rawFile.last_accessed_at,
    };
  }

  private static async hydrateMediaPreviewUrls(files: FileData[]): Promise<FileData[]> {
    return Promise.all(
      files.map(async (file) => {
        if (!this.canPreviewAsMedia(file.mimeType)) {
          return file;
        }

        const previewSourceUrl = file.thumbnailUrl || file.url;
        if (!previewSourceUrl || /^(blob:|data:)/.test(previewSourceUrl)) {
          return file;
        }

        const absolutePreviewSourceUrl = this.toAbsoluteUrl(previewSourceUrl);
        if (!this.isProtectedApiFileUrl(absolutePreviewSourceUrl)) {
          return {
            ...file,
            thumbnailUrl: absolutePreviewSourceUrl,
          };
        }

        try {
          return this.ownMediaPreviewUrl(
            { ...file },
            await this.createAuthenticatedObjectUrl(absolutePreviewSourceUrl),
          );
        } catch (error) {
          console.warn('Failed to hydrate media preview URL', file.id, error);
          return {
            ...file,
            thumbnailUrl: undefined,
          };
        }
      })
    );
  }

  private static async buildUploadError(response: Response, fallbackMessage: string): Promise<Error> {
    let detail = '';

    try {
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const payload = await response.json();
        detail = typeof payload?.detail === 'string' ? payload.detail : '';
      } else {
        detail = await response.text();
      }
    } catch {
      detail = '';
    }

    const message = detail.trim() || `${fallbackMessage}: HTTP ${response.status}`;
    return new Error(message);
  }

  /**
   * Read every page of a file list route. The routes cap a page at 100 files and report the
   * total; reading only the first page silently dropped the rest of a large project's files.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API response items have dynamic shape
  private static async fetchAllFilePages(path: string, failure: (response: Response) => Error): Promise<any[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API response items have dynamic shape
    const collected: any[] = [];
    for (let page = 1; page <= FILE_LIST_MAX_PAGES; page += 1) {
      const separator = path.includes('?') ? '&' : '?';
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}${path}${separator}page=${page}&limit=${FILE_LIST_PAGE_SIZE}`
      );
      if (!response.ok) {
        throw failure(response);
      }
      const data = await response.json();
      if (Array.isArray(data)) return data;
      const files = Array.isArray(data?.files) ? data.files : [];
      collected.push(...files);
      const total = typeof data?.total === 'number' ? data.total : null;
      if (total === null || collected.length >= total || files.length < FILE_LIST_PAGE_SIZE) break;
    }
    return collected;
  }

  static async getDocumentFiles(projectId: string, documentId: string): Promise<FileData[]> {
    const files = await this.fetchAllFilePages(
      API_ENDPOINTS.files.list(projectId, documentId),
      () => new Error('Failed to fetch files'),
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API response items have dynamic shape
    const normalized = files.map((file: any) =>
      this.normalizeFile(file, (fileId) => API_ENDPOINTS.files.download(projectId, documentId, fileId))
    );
    return this.hydrateMediaPreviewUrls(normalized);
  }

  static async getProjectFiles(
    projectId: string,
    options: { hydrateMediaPreviews?: boolean } = {},
  ): Promise<FileData[]> {
    const files = await this.fetchAllFilePages(
      API_ENDPOINTS.files.projectFiles(projectId),
      (response) => Object.assign(new Error(`Failed to fetch project files: ${response.status}`), { status: response.status }),
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API response items have dynamic shape
    const normalized = files.map((file: any) =>
      this.normalizeFile(file, (fileId) => API_ENDPOINTS.files.projectFileDownload(fileId))
    );
    return options.hydrateMediaPreviews === false
      ? normalized
      : this.hydrateMediaPreviewUrls(normalized);
  }

  static async getHtmlArtifactPreview(
    fileId: string,
    expectedSha256: string,
    options: RequestInit = {},
  ): Promise<HtmlArtifactPreview> {
    const url = `${API_BASE_URL}${API_ENDPOINTS.files.htmlArtifactPreview(fileId)}`;
    const response = Object.keys(options).length
      ? await authService.authenticatedFetch(url, options)
      : await authService.authenticatedFetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load secure HTML artifact preview: ${response.status}`);
    }
    const payload = await response.json() as Partial<HtmlArtifactPreview>;
    const checksum = String(payload.sha256 ?? '').toLowerCase();
    const expected = String(expectedSha256 ?? '').toLowerCase();
    const valid = payload.fileId === fileId
      && payload.mimeType === 'text/html'
      && payload.sandbox === ''
      && payload.referrerPolicy === 'no-referrer'
      && payload.contentSecurityPolicy === HTML_ARTIFACT_CSP
      && typeof payload.previewDocument === 'string'
      && payload.previewDocument.includes(HTML_ARTIFACT_CSP)
      && /^[a-f0-9]{64}$/.test(checksum)
      && /^[a-f0-9]{64}$/.test(expected)
      && expected === checksum
      && Number.isInteger(payload.size)
      && Number(payload.size) >= 0;
    if (!valid) {
      throw new Error('Secure HTML preview contract failed validation');
    }
    return payload as HtmlArtifactPreview;
  }

  private static async uploadBatch(
    files: File[],
    upload: (file: File) => Promise<FileData>,
  ): Promise<FileData[]> {
    const uploadedFiles: FileData[] = [];
    let failed = false;
    try {
      return await Promise.all(files.map(async (file) => {
        const uploaded = await upload(file);
        if (failed) {
          this.disposeMediaPreviews([uploaded]);
        } else {
          uploadedFiles.push(uploaded);
        }
        return uploaded;
      }));
    } catch (error) {
      failed = true;
      this.disposeMediaPreviews(uploadedFiles);
      throw error;
    }
  }

  static async uploadFiles(
    projectId: string,
    documentId: string,
    files: File[]
  ): Promise<FileData[]> {
    return this.uploadBatch(files, (file) => this.uploadFile(projectId, documentId, file));
  }
  
  static async uploadFile(
    projectId: string, 
    documentId: string, 
    file: File
  ): Promise<FileData> {
    const normalizedFile = await this.normalizeUploadFile(file);
    const formData = new FormData();
    formData.append('file', normalizedFile);
    
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.files.upload(projectId, documentId)}`,
      {
        method: 'POST',
        body: formData
      }
    );
    
    if (!response.ok) {
      throw await this.buildUploadError(response, 'Failed to upload file');
    }
    
    const data = await response.json();
    const normalized = this.normalizeFile(
      data,
      (fileId) => API_ENDPOINTS.files.download(projectId, documentId, fileId)
    );

    if (this.canPreviewAsMedia(normalized.mimeType)) {
      this.ownMediaPreviewUrl(normalized, URL.createObjectURL(normalizedFile));
    }

    return normalized;
  }
  
  static async deleteFile(
    projectId: string,
    documentId: string,
    fileId: string
  ): Promise<void> {
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.files.delete(projectId, documentId, fileId)}`,
      {
        method: 'DELETE'
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to delete file');
    }
  }

  static async getNodeFiles(projectId: string, nodeId: string): Promise<FileData[]> {
    const files = await this.fetchAllFilePages(
      API_ENDPOINTS.files.nodeList(projectId, nodeId),
      () => new Error('Failed to fetch node files'),
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API response items have dynamic shape
    const normalized = files.map((file: any) =>
      this.normalizeFile(file, (fileId) => API_ENDPOINTS.files.nodeDownload(fileId))
    );
    return this.hydrateMediaPreviewUrls(normalized);
  }

  static async uploadNodeFiles(
    projectId: string,
    nodeId: string,
    files: File[]
  ): Promise<FileData[]> {
    return this.uploadBatch(files, (file) => this.uploadNodeFile(projectId, nodeId, file));
  }

  static async uploadNodeFile(
    projectId: string,
    nodeId: string,
    file: File
  ): Promise<FileData> {
    const normalizedFile = await this.normalizeUploadFile(file);
    const formData = new FormData();
    formData.append('file', normalizedFile);

    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.files.nodeUpload(projectId, nodeId)}`,
      {
        method: 'POST',
        body: formData
      }
    );

    if (!response.ok) {
      throw await this.buildUploadError(response, 'Failed to upload node file');
    }

    const data = await response.json();
    const normalized = this.normalizeFile(data, (fileId) => API_ENDPOINTS.files.nodeDownload(fileId));

    if (this.canPreviewAsMedia(normalized.mimeType)) {
      this.ownMediaPreviewUrl(normalized, URL.createObjectURL(normalizedFile));
    }

    return normalized;
  }

  static async deleteNodeFile(
    projectId: string,
    nodeId: string,
    fileId: string
  ): Promise<void> {
    void projectId;
    void nodeId;
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.files.nodeDelete(fileId)}`,
      {
        method: 'DELETE'
      }
    );

    if (!response.ok) {
      throw new Error('Failed to delete node file');
    }
  }
  
  static isImageFile(mimeType: string): boolean {
    return this.normalizeMimeType(mimeType).startsWith('image/');
  }
  
  static isPdfFile(mimeType: string, filename = ''): boolean {
    const normalized = this.normalizeMimeType(mimeType);
    return normalized === 'application/pdf'
      || ((!normalized || normalized === 'application/octet-stream') && /\.pdf$/i.test(filename.trim()));
  }
  
  static isVideoFile(mimeType: string): boolean {
    return this.normalizeMimeType(mimeType).startsWith('video/');
  }

  static isAudioFile(mimeType: string): boolean {
    return this.normalizeMimeType(mimeType).startsWith('audio/');
  }

  static isTextFile(mimeType: string, filename = ''): boolean {
    const normalized = this.normalizeMimeType(mimeType);
    const normalizedName = filename.toLowerCase();
    return (
      normalized.startsWith('text/') ||
      normalized.includes('json') ||
      normalized.includes('xml') ||
      normalized.includes('csv') ||
      /\.(txt|md|markdown|csv|json|xml|log)$/.test(normalizedName)
    );
  }

  static isHtmlArtifactFile(file: Pick<FileData, 'mimeType' | 'originalName'>): boolean {
    const mimeType = this.normalizeMimeType(file.mimeType);
    return mimeType === 'text/html' && /\.html?$/i.test(file.originalName.trim());
  }

  static isDocumentPreviewFile(mimeType: string): boolean {
    const normalized = this.normalizeMimeType(mimeType);
    return (
      this.isPdfFile(normalized) ||
      normalized.startsWith('text/') ||
      normalized.includes('json') ||
      normalized.includes('xml') ||
      normalized.includes('csv') ||
      normalized.includes('word') ||
      normalized.includes('document') ||
      normalized.includes('spreadsheet') ||
      normalized.includes('excel') ||
      normalized.includes('presentation') ||
      normalized.includes('powerpoint') ||
      normalized.includes('opendocument') ||
      normalized.includes('hancom') ||
      normalized.includes('hwp')
    );
  }

  static isOfficeRenderedPreviewFile(mimeType: string, filename = ''): boolean {
    const normalized = this.normalizeMimeType(mimeType);
    const normalizedName = filename.toLowerCase();
    return (
      /\.(doc|docx|dot|dotx|xls|xlsx|xlsm|xlt|xltx|ppt|pptx|pot|potx|odt|ods|odp|hwpx)$/.test(normalizedName) ||
      normalized.includes('wordprocessingml') ||
      normalized.includes('spreadsheetml') ||
      normalized.includes('presentationml') ||
      normalized.includes('opendocument') ||
      normalized.includes('msword') ||
      normalized.includes('excel') ||
      normalized.includes('powerpoint') ||
      normalized.includes('hancom') ||
      normalized.includes('hwp')
    );
  }

  static isRenderedDocumentPreviewFile(file: Pick<FileData, 'mimeType' | 'originalName'>): boolean {
    return (
      this.isPdfFile(file.mimeType, file.originalName) ||
      this.isTextFile(file.mimeType, file.originalName) ||
      this.isOfficeRenderedPreviewFile(file.mimeType, file.originalName)
    );
  }

  static canPreviewAsMedia(mimeType: string): boolean {
    return this.isImageFile(mimeType) || this.isVideoFile(mimeType) || this.isAudioFile(mimeType);
  }

  // Every attachment gets a preview shell, including an unavailable/download
  // fallback. This does not promise a renderer for every MIME type.
  static canInlinePreview(mimeType: string): boolean {
    void mimeType;
    return true;
  }

  static toAbsoluteUrl(url: string): string {
    if (/^(blob:|data:)/.test(url)) {
      return url;
    }
    if (/^https?:\/\//.test(url)) {
      return url;
    }
    if (url.startsWith('/')) {
      return `${API_BASE_URL}${url}`;
    }
    return `${API_BASE_URL}/${url}`;
  }

  static isProtectedApiFileUrl(url: string): boolean {
    const absoluteUrl = this.toAbsoluteUrl(url);
    return absoluteUrl.startsWith(`${API_BASE_URL}/api/v1/files/`);
  }

  static toPreviewUrl(url: string): string {
    const absoluteUrl = this.toAbsoluteUrl(url);
    if (/\/preview(?:$|[?#])/.test(absoluteUrl)) {
      return absoluteUrl;
    }
    if (/\/download(?:$|[?#])/.test(absoluteUrl)) {
      return absoluteUrl.replace(/\/download(?=$|[?#])/, '/preview');
    }
    return absoluteUrl;
  }

  static getRenderedPreviewSourceUrl(file: Pick<FileData, 'id' | 'url' | 'mimeType' | 'originalName'>): string {
    if (file.url && (this.isPdfFile(file.mimeType, file.originalName) || this.isOfficeRenderedPreviewFile(file.mimeType, file.originalName))) {
      return this.toPreviewUrl(file.url);
    }
    if (file.url && this.isTextFile(file.mimeType, file.originalName)) {
      return this.toAbsoluteUrl(file.url);
    }
    return this.toAbsoluteUrl(`/api/v1/files/${file.id}/preview`);
  }

  static async fetchPreviewBlob(url: string, options: RequestInit = {}): Promise<Blob> {
    const absoluteUrl = this.toAbsoluteUrl(url);
    const response = this.isProtectedApiFileUrl(absoluteUrl)
      ? await authService.authenticatedFetch(absoluteUrl, options)
      : await fetch(absoluteUrl, options);

    if (!response.ok) {
      throw new Error(`Failed to resolve preview bytes: ${response.status}`);
    }

    return response.blob();
  }

  static async createAuthenticatedObjectUrl(url: string): Promise<string> {
    const absoluteUrl = this.toAbsoluteUrl(url);
    const response = await authService.authenticatedFetch(absoluteUrl);

    if (!response.ok) {
      throw new Error(`Failed to resolve protected media URL: ${response.status}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  static async downloadProtectedUrl(url: string, filename?: string): Promise<void> {
    const absoluteUrl = this.toAbsoluteUrl(url);
    const response = await authService.authenticatedFetch(absoluteUrl);

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.status}`);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename || 'download';
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
  
  static async downloadFile(
    projectId: string,
    documentId: string,
    fileId: string
  ): Promise<Blob> {
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.files.download(projectId, documentId, fileId)}`
    );
    
    if (!response.ok) {
      throw new Error('Failed to download file');
    }
    
    return response.blob();
  }
  
  static getFileIcon(mimeType: string): string {
    const normalized = this.normalizeMimeType(mimeType);
    if (this.isImageFile(mimeType)) return '🖼️';
    if (this.isPdfFile(mimeType)) return '📄';
    if (this.isVideoFile(mimeType)) return '🎥';
    if (this.isAudioFile(mimeType)) return '🎧';
    if (normalized.includes('word')) return '📝';
    if (normalized.includes('excel') || normalized.includes('spreadsheet')) return '📊';
    if (normalized.includes('powerpoint') || normalized.includes('presentation')) return '📑';
    return '📎';
  }
}
